import { Col, Form, Input, InputNumber, Modal, Row, Select, Switch, notification } from 'antd';
import { useEffect, useState } from 'react';
import CustomButton from 'components/custom-button';
import CustomizedUpload from 'components/customized-upload';
import { UPLOAD_TYPE } from 'constants/common';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import {
  ITEM_CATEGORY_OPTIONS,
  ITEM_KIND_OPTIONS,
  ITEM_STATUS_OPTIONS,
  STAR_GROUP_OPTIONS,
} from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import { ItemAdmin, ItemKind } from 'pages/star-shared/types';
import { imageUrl } from 'pages/star-shared/utils';

interface Props {
  open: boolean;
  item: ItemAdmin | null;
  // clone=true: mở form tạo mới với dữ liệu chép từ `item`.
  clone?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const IMAGE_RULE = {
  validator: (_: any, value: { error?: string } | undefined) =>
    value?.error ? Promise.reject(new Error(String(value.error))) : Promise.resolve(),
};

const ItemModal = ({ open, item, clone = false, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const kind = Form.useWatch('kind', form) as ItemKind | undefined;
  const isEdit = !!item && !clone;

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: item ? (clone ? `${item.name} (bản sao)` : item.name) : '',
      description: item?.description || '',
      image: item?.image ? { fileKey: item.image, fileUrl: imageUrl(item.image) } : undefined,
      category: item?.category || 'DRINK',
      kind: item?.kind || 'GOODS',
      price: item?.price ?? 10,
      stock: item?.stock ?? null,
      perUserDailyLimit: item?.perUserDailyLimit ?? null,
      perUserMonthlyLimit: item?.perUserMonthlyLimit ?? null,
      needsApproval: item?.needsApproval ?? false,
      starGroups: item?.audience?.starGroups || [],
      validDays: item?.validDays ?? null,
      transferMax: item?.transferMax ?? null,
      status: clone ? 'HIDDEN' : item?.status || 'ACTIVE',
      sort: item?.sort ?? 0,
    });
  }, [open, item, clone, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: any) => {
    const isCard = values.kind === 'TRANSFER_CARD';
    const hasValidity = values.kind !== 'GOODS';
    const payload = {
      name: values.name.trim(),
      description: values.description?.trim() || null,
      image: values.image?.fileKey || null,
      category: values.category,
      kind: values.kind,
      price: values.price,
      stock: values.stock ?? null,
      perUserDailyLimit: values.perUserDailyLimit ?? null,
      perUserMonthlyLimit: values.perUserMonthlyLimit ?? null,
      needsApproval: !!values.needsApproval,
      audience: { starGroups: values.starGroups || [] },
      validDays: hasValidity ? values.validDays ?? null : null,
      transferMax: isCard ? values.transferMax ?? null : null,
      status: values.status,
      sort: values.sort ?? 0,
    };
    setLoading(true);
    try {
      if (isEdit && item) {
        await AxiosUtils.patch(`${API.starsItems}/${item.id}`, payload);
        notification.success({ message: 'Đã cập nhật món quà.' });
      } else {
        await AxiosUtils.post(API.starsItems, payload);
        notification.success({ message: clone ? 'Đã nhân bản món quà (đang ẩn).' : 'Đã tạo món quà.' });
      }
      onSuccess();
      handleClose();
    } catch (err) {
      notification.error({ message: mapStarError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isEdit ? 'Sửa món quà' : clone ? 'Nhân bản món quà' : 'Thêm món quà'}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={800}
    >
      <Form form={form} layout="vertical" onFinish={onFinish} className="custom-form">
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              label="Tên món"
              name="name"
              rules={[
                { required: true, whitespace: true, message: 'Vui lòng nhập tên món.' },
                { max: 120, message: 'Tối đa 120 ký tự.' },
              ]}
            >
              <Input placeholder="VD: Trà sữa trân châu" maxLength={120} />
            </Form.Item>
            <Form.Item label="Mô tả" name="description" rules={[{ max: 500, message: 'Tối đa 500 ký tự.' }]}>
              <Input.TextArea rows={3} maxLength={500} showCount placeholder="Điều kiện nhận, cách dùng" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Ảnh" name="image" rules={[IMAGE_RULE]}>
              <CustomizedUpload type={UPLOAD_TYPE.IMAGE} maxFileSize={5} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Danh mục" name="category" rules={[{ required: true }]}>
              <Select options={ITEM_CATEGORY_OPTIONS} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Loại" name="kind" rules={[{ required: true }]}>
              <Select options={ITEM_KIND_OPTIONS} disabled={isEdit} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Giá (Sao)"
              name="price"
              rules={[{ required: true, type: 'integer', min: 0, message: 'Số nguyên ≥ 0.' }]}
            >
              <InputNumber size="large" style={{ width: '100%' }} min={0} precision={0} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Tồn kho" name="stock" extra="Để trống = không giới hạn.">
              <InputNumber size="large" style={{ width: '100%' }} min={0} precision={0} placeholder="Không giới hạn" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Tối đa/người/ngày" name="perUserDailyLimit" extra="Để trống = không giới hạn.">
              <InputNumber size="large" style={{ width: '100%' }} min={1} precision={0} placeholder="Không giới hạn" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Tối đa/người/tháng" name="perUserMonthlyLimit" extra="Để trống = không giới hạn.">
              <InputNumber size="large" style={{ width: '100%' }} min={1} precision={0} placeholder="Không giới hạn" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Cần duyệt khi đổi" name="needsApproval" valuePropName="checked">
              <Switch checkedChildren="Có" unCheckedChildren="Không" />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item label="Nhóm Sao được đổi" name="starGroups" extra="Để trống = mọi nhóm.">
              <Select mode="multiple" allowClear options={STAR_GROUP_OPTIONS} placeholder="Mọi nhóm" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          {kind !== 'GOODS' && (
            <Col span={8}>
              <Form.Item
                label="Hiệu lực (ngày)"
                name="validDays"
                rules={[{ required: true, type: 'integer', min: 1, message: 'Số ngày ≥ 1.' }]}
                extra="Thẻ/đặc quyền hết hạn sau số ngày này."
              >
                <InputNumber size="large" style={{ width: '100%' }} min={1} precision={0} />
              </Form.Item>
            </Col>
          )}
          {kind === 'TRANSFER_CARD' && (
            <Col span={8}>
              <Form.Item
                label="Tặng tối đa (Sao/thẻ)"
                name="transferMax"
                rules={[{ required: true, type: 'integer', min: 1, message: 'Số nguyên ≥ 1.' }]}
              >
                <InputNumber size="large" style={{ width: '100%' }} min={1} precision={0} />
              </Form.Item>
            </Col>
          )}
          <Col span={8}>
            <Form.Item label="Trạng thái" name="status" rules={[{ required: true }]}>
              <Select options={ITEM_STATUS_OPTIONS} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Thứ tự" name="sort">
              <InputNumber size="large" style={{ width: '100%' }} precision={0} />
            </Form.Item>
          </Col>
        </Row>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text={isEdit ? 'Lưu' : 'Tạo'}
            bgColor="secondary-green"
            htmlType="submit"
            loading={loading}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default ItemModal;
